import { useMemo } from 'react'
import { Form, Switch, Divider } from '@arco-design/web-react'
import SettingSectionCard from '../SettingSectionCard'
import type { OptionsAPI } from '../useOptions'
import {
  DEFAULT_SIDEBAR_MODULES,
  SIDEBAR_ITEM_LABELS,
  SIDEBAR_SECTION_LABELS,
  parseSidebarModules,
  serializeSidebarModules,
  type SidebarModules,
} from '@/constants/sidebarModules'

const KEYS = ['SidebarModulesAdmin']

export default function SidebarModulesAdminSection({ options }: { options: OptionsAPI }) {
  const { get, patch, values, dataRevision } = options

  const modules = useMemo(
    () => parseSidebarModules(get('SidebarModulesAdmin')),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dataRevision tracks server reload
    [values.SidebarModulesAdmin, dataRevision]
  )

  const updateModules = (next: SidebarModules) => {
    patch({ SidebarModulesAdmin: serializeSidebarModules(next) })
  }

  const setSectionEnabled = (section: string, enabled: boolean) => {
    const next = structuredClone(modules)
    const group = next[section] as { enabled?: boolean; items?: Record<string, { enabled?: boolean }> } | undefined
    if (!group) return
    group.enabled = enabled
    updateModules(next)
  }

  const setItemEnabled = (section: string, item: string, enabled: boolean) => {
    const next = structuredClone(modules)
    const group = next[section] as { enabled?: boolean; items?: Record<string, { enabled?: boolean }> } | undefined
    if (!group?.items?.[item]) return
    group.items[item] = { ...group.items[item], enabled }
    updateModules(next)
  }

  const resetToDefault = () => {
    updateModules(structuredClone(DEFAULT_SIDEBAR_MODULES))
  }

  return (
    <SettingSectionCard
      title="侧边栏模块"
      subtitle="可视化控制控制台侧边栏可见项，保存至 SidebarModulesAdmin"
      keys={KEYS}
      options={options}
    >
      <Form layout="vertical">
        {Object.keys(SIDEBAR_SECTION_LABELS).map((section) => {
          const group = modules[section] as { enabled?: boolean; items?: Record<string, { enabled?: boolean }> } | undefined
          const sectionEnabled = group?.enabled !== false
          const itemLabels = SIDEBAR_ITEM_LABELS[section] ?? {}

          return (
            <div key={section} className="mb-4">
              <Divider orientation="left">{SIDEBAR_SECTION_LABELS[section]}</Divider>
              <Form.Item label="分组可见">
                <Switch checked={sectionEnabled} onChange={(v) => setSectionEnabled(section, v)} />
              </Form.Item>
              <div className="grid md:grid-cols-2 gap-3">
                {Object.entries(itemLabels).map(([itemKey, label]) => (
                  <Form.Item key={itemKey} label={label}>
                    <Switch
                      checked={group?.items?.[itemKey]?.enabled !== false}
                      disabled={!sectionEnabled}
                      onChange={(v) => setItemEnabled(section, itemKey, v)}
                    />
                  </Form.Item>
                ))}
              </div>
            </div>
          )
        })}
        <Form.Item>
          <button type="button" className="text-sm text-blue-600 hover:underline" onClick={resetToDefault}>
            恢复默认侧栏配置
          </button>
        </Form.Item>
      </Form>
    </SettingSectionCard>
  )
}
